import { useState, useRef } from 'react';
import { Upload, Download, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';

interface ImportError {
  row: number;
  error: string;
}

interface ImportResult {
  success: number;
  failed: number;
  errors: ImportError[];
  message: string;
}

interface CSVImportExportProps {
  onImportComplete?: () => void;
  includeInactive?: boolean;
}

export function CSVImportExport({ onImportComplete, includeInactive = false }: CSVImportExportProps) {
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const response = await fetch(`/api/admin/products/export?includeInactive=${includeInactive}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateStr = new Date().toISOString().split('T');
      a.download = `products_export_${dateStr[0] || 'download'}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export products');
    }
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await fetch('/api/admin/products/template', {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      if (!response.ok) {
        throw new Error('Template download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'products_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Template download error:', error);
      alert('Failed to download template');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportResult({
        success: 0,
        failed: 1,
        errors: [{ row: 0, error: 'Please select a CSV file' }],
        message: 'Invalid file type. Please select a CSV file.',
      });
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setImportResult({
        success: 0,
        failed: 1,
        errors: [{ row: 0, error: 'File size exceeds 5MB limit' }],
        message: 'File is too large. Maximum size is 5MB.',
      });
      return;
    }

    setIsImporting(true);
    setUploadProgress(0);
    setImportResult(null);

    try {
      // Read file as UTF-8 text
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            resolve(e.target.result as string);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file, 'UTF-8');
      });
      setUploadProgress(50);

      const response = await fetch('/api/admin/products/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          csvContent: text,
        }),
      });

      setUploadProgress(100);
      const result = await response.json();

      if (response.ok) {
        setImportResult(result);
        if (result.success > 0 && onImportComplete) {
          onImportComplete();
        }
      } else {
        setImportResult({
          success: 0,
          failed: 1,
          errors: [{ row: 0, error: result.message || 'Import failed' }],
          message: result.message || 'Import failed',
        });
      }
    } catch (error) {
      setImportResult({
        success: 0,
        failed: 1,
        errors: [{ row: 0, error: error instanceof Error ? error.message : 'Unknown error' }],
        message: 'Failed to import CSV file',
      });
    } finally {
      setIsImporting(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      <div className="flex gap-2">
        <Button
          onClick={handleExport}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Export CSV
        </Button>

        <Button
          onClick={() => setShowImportDialog(true)}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Upload className="h-4 w-4" />
          Import CSV
        </Button>

        <Button
          onClick={handleTemplateDownload}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          Download Template
        </Button>
      </div>

      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Import Products from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to import products. You can download a template to see the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={isImporting}
                className="hidden"
                id="csv-file-input"
              />
              <Label
                htmlFor="csv-file-input"
                className={`inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-10 px-4 py-2 gap-2 cursor-pointer ${
                  isImporting
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
              >
                <Upload className="h-4 w-4" />
                {isImporting ? 'Importing...' : 'Choose CSV File'}
              </Label>
            </div>

            {isImporting && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Importing products...</p>
                <Progress value={uploadProgress} />
              </div>
            )}

            {importResult && (
              <Alert variant={importResult.failed > 0 ? "destructive" : "default"}>
                <div className="flex items-start gap-2">
                  {importResult.failed > 0 ? (
                    <AlertCircle className="h-4 w-4 mt-0.5" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <AlertDescription>
                      <p className="font-semibold">{importResult.message}</p>
                      {importResult.errors.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-sm">Errors:</p>
                          <ul className="text-sm list-disc list-inside max-h-40 overflow-y-auto">
                            {importResult.errors.map((error, idx) => (
                              <li key={idx}>
                                Row {error.row}: {error.error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </AlertDescription>
                  </div>
                </div>
              </Alert>
            )}

            <div className="text-sm text-muted-foreground">
              <p>Simple CSV format with only 6 columns:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li><strong>Required:</strong> category, description, size, retailPrice, ourPrice</li>
                <li><strong>Optional:</strong> chineseName</li>
                <li className="text-green-600 font-medium">✓ UTF-8 encoding supported for Chinese characters</li>
                <li className="text-blue-600">Categories: produce, meat-beef, meat-chicken, meat-pork, meat-hotpot, seafood, groceries</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}